<script setup>
import { computed } from "vue";
import { useRouter } from "vue-router";
import { MessageCircle, Mail, FileText } from "lucide-vue-next";
import Logo2 from "@/images/Logos/02_Logo.png";

const router = useRouter();

const navLinks = [
  { name: "Compra tu auto", to: "/catalogo/0/0/0" },
  { name: "Contacto", to: "/contacto" },
  { name: "Nosotros", to: "/recomendaciones" },
  { name: "Tomas", to: "/" },
  { name: "Blog", to: "/blog" },
];

const legalLinks = [
  {
    name: "Aviso de privacidad",
    href: "https://www.car4u.mx/docs/Aviso%20de%20privacidad%20y%20protecci%C3%B3n%20de%20datos%20personales%20Car4U.pdf",
  },
  {
    name: "Términos y condiciones",
    href: "https://www.car4u.mx/docs/Aviso%20Legal%20y%20Condiciones%20de%20Uso%20Car4U.pdf",
  },
];

const currentYear = computed(() => new Date().getFullYear());

const goHome = () => router.push("/");
const setWhatsApp = () => {
  window.open("https://api.whatsapp.com/send?phone=4494551588", "_blank");
};
</script>

<template>
  <footer class="c4u-footer">
    <div class="c4u-footer__main">
      <div class="c4u-footer__brand">
        <button type="button" class="c4u-footer__logo-btn" @click="goHome" aria-label="CAR4U — Ir al inicio">
          <img :src="Logo2" alt="CAR4U" class="c4u-footer__logo" width="120" height="56" />
        </button>
        <p class="c4u-footer__tagline">Autos seminuevos con confianza, seguridad y garantía.</p>
      </div>

      <div class="c4u-footer__col">
        <h3 class="c4u-footer__col-title">Contacto</h3>
        <ul class="c4u-footer__list">
          <li>
            <button type="button" class="c4u-footer__link c4u-footer__link--button" @click="setWhatsApp">
              <MessageCircle :size="16" :stroke-width="1.75" aria-hidden="true" />
              449 455 1588
            </button>
          </li>
          <li>
            <a href="mailto:atencionclientes@car4u.mx" class="c4u-footer__link">
              <Mail :size="16" :stroke-width="1.75" aria-hidden="true" />
              atencionclientes@car4u.mx
            </a>
          </li>
        </ul>
      </div>

      <div class="c4u-footer__col">
        <h3 class="c4u-footer__col-title">Legal</h3>
        <ul class="c4u-footer__list">
          <li v-for="item in legalLinks" :key="item.href">
            <a :href="item.href" class="c4u-footer__link">
              <FileText :size="16" :stroke-width="1.75" aria-hidden="true" />
              {{ item.name }}
            </a>
          </li>
        </ul>
      </div>
    </div>

    <div class="c4u-footer__bottom">
      <p>© {{ currentYear }} CAR4U. Todos los derechos reservados.</p>
    </div>
  </footer>
</template>

<style scoped>
.c4u-footer {
  background: var(--c4u-black);
  color: var(--c4u-text-on-dark);
}

.c4u-footer__main {
  max-width: var(--c4u-container);
  margin-inline: auto;
  padding: var(--c4u-space-xl) var(--c4u-gutter);
  display: grid;
  grid-template-columns: 1fr;
  gap: var(--c4u-space-lg);
}

.c4u-footer__brand {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: var(--c4u-space-xs);
}

.c4u-footer__logo-btn {
  background: none;
  border: none;
  padding: 0;
  margin-inline: -4px;
  padding-inline: 4px;
  cursor: pointer;
  border-radius: var(--c4u-radius-sm);
}

.c4u-footer__logo-btn:focus-visible {
  outline: 2px solid var(--c4u-focus-ring);
  outline-offset: 3px;
}

.c4u-footer__logo {
  height: 40px;
  width: auto;
  object-fit: contain;
}

.c4u-footer__tagline {
  margin: 0;
  max-width: 32ch;
  color: var(--c4u-text-on-dark-muted);
  font-size: var(--c4u-fs-label);
  line-height: var(--c4u-lh-normal);
}

.c4u-footer__col-title {
  margin: 0 0 var(--c4u-space-sm);
  font-family: var(--c4u-font-display);
  font-weight: 600;
  font-size: var(--c4u-fs-label);
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: var(--c4u-text-on-dark-muted);
}

.c4u-footer__list {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: var(--c4u-space-sm);
}

.c4u-footer__link {
  display: inline-flex;
  align-items: center;
  gap: var(--c4u-space-xs);
  background: none;
  border: none;
  padding: 0;
  color: var(--c4u-text-on-dark);
  text-decoration: none;
  font-family: var(--c4u-font-body);
  font-size: var(--c4u-fs-body);
  cursor: pointer;
  transition: color var(--c4u-dur-fast) var(--c4u-ease-out);
}

.c4u-footer__link svg {
  color: var(--c4u-green-light);
  flex-shrink: 0;
}

.c4u-footer__link:hover {
  color: var(--c4u-green-light);
}

.c4u-footer__link:focus-visible {
  outline: 2px solid var(--c4u-focus-ring);
  outline-offset: 2px;
}

.c4u-footer__bottom {
  border-top: 1px solid var(--c4u-border-on-dark);
  padding: var(--c4u-space-md) var(--c4u-gutter);
}

.c4u-footer__bottom p {
  max-width: var(--c4u-container);
  margin-inline: auto;
  color: var(--c4u-text-on-dark-muted);
  font-size: 0.8125rem;
}

@media (min-width: 768px) {
  .c4u-footer__main {
    grid-template-columns: 1.4fr 1fr 1fr;
    gap: var(--c4u-space-xl);
  }
}
</style>
